import { z } from 'zod';
import { evaluate } from './conditions.js';
import { answeredCredit } from './conflicts.js';
import { AgentProviderSpec } from './llm-providers.js';
/**
 * AI follow-up questions (task 87, decision 0064). After a business answers, the follow-up agent proposes
 * clarifying questions, each grounded in the control library, where an answer:
 *
 *  - is vague: a free-text reply (a probe's "why" or "when", a typed answer) that doesn't say what was asked;
 *  - contradicts another answer: a control is in place although the one it rests on isn't (the journey's
 *    own routes say which, found here without a model);
 *  - contradicts a document: a claim check found the business's documents say otherwise;
 *  - names a risk: the business's own words mention one (an old system, a past incident, a shared login).
 *
 * The business replies in its own words. The agent reads what the reply means for the answer, but nothing
 * changes the score until an underwriter confirms the change; the reply is kept either way.
 */
export const FOLLOW_UP_REASONS = [
    'vague',
    'contradicts_answer',
    'contradicts_document',
    'names_risk',
];
export const FOLLOW_UP_REASON_LABEL = {
    vague: 'Vague answer',
    contradicts_answer: 'Contradicts another answer',
    contradicts_document: 'Contradicts a document',
    names_risk: 'Names a risk',
};
/**
 * - open: waiting for the business's reply;
 * - answered: replied, with what the agent reads it as;
 * - confirmed: an underwriter applied the change the reply supports;
 * - kept: an underwriter kept the answer as it was (the reply stays on file);
 * - dismissed: an underwriter withdrew it before a reply.
 */
export const FOLLOW_UP_STATES = ['open', 'answered', 'confirmed', 'kept', 'dismissed'];
export const FOLLOW_UP_STATE_LABEL = {
    open: 'Waiting for the business',
    answered: 'Replied',
    confirmed: 'Answer changed',
    kept: 'Answer kept',
    dismissed: 'Withdrawn',
};
/** At most this many open follow-ups per assessment, so the business isn't buried in them. */
export const MAX_OPEN_FOLLOW_UPS = 8;
/** A reading of a reply below this confidence isn't offered as a change. */
export const FOLLOW_UP_MIN_CONFIDENCE = 0.6;
export const ReplyFollowUp = z.strictObject({ text: z.string().trim().min(1, 'Write a reply').max(2000) });
export const DecideFollowUp = z.strictObject({
    action: z.enum(['confirm', 'keep', 'dismiss']),
    note: z.string().trim().max(1000).optional(),
});
/**
 * Answers that say a control is in place although an answer it rests on says otherwise. A route that skips
 * questions as "not in place" (task 85) is exactly that dependency: when its condition holds, an answer
 * crediting one of the questions it skips contradicts it. Pure; answers by question key.
 */
export function answerContradictions(questions, answers, facts = {}) {
    const out = [];
    for (const [key, q] of Object.entries(questions)) {
        const value = answers[key];
        if (value === undefined || value === null)
            continue;
        const controls = q.metadata.controls ?? [];
        const credited = controls.some((c) => (answeredCredit(q, value, c) ?? 0) > 0);
        if (!credited)
            continue;
        for (const g of q.skipped_by ?? []) {
            if (g.as !== 'not_in_place' || answers[g.from] === undefined)
                continue;
            if (evaluate(g.when, { answers, facts })) {
                out.push({ question_key: key, related: g.from, detail: g.label });
                break;
            }
        }
    }
    return out.sort((a, b) => a.question_key.localeCompare(b.question_key));
}
// —— the agent (POST /v1/followups/propose and /v1/followups/interpret, `followups.py`) ——
/** One answer the agent may follow up, with the business's own words where it gave any. */
export const AgentAnswerItem = z.strictObject({
    question_key: z.string().min(1).max(64),
    question: z.string().min(1).max(500),
    answer: z.string().max(500),
    /** Free text: a typed answer, or the reply to a probe on this question. */
    text: z.string().max(2000).default(''),
    control: z.string().max(16).nullable().default(null),
});
/** A contradiction found without a model, for the agent to phrase. */
export const AgentContradiction = z.strictObject({
    question_key: z.string().min(1).max(64),
    reason: z.enum(['contradicts_answer', 'contradicts_document']),
    related: z.string().max(200),
    detail: z.string().max(500),
});
export const FollowUpRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    language: z.enum(['en', 'ms']).default('en'),
    items: z.array(AgentAnswerItem).max(400),
    contradictions: z.array(AgentContradiction).max(50).default([]),
    /** Questions already followed up, not to ask about again. */
    skip: z.array(z.string().max(64)).max(400).default([]),
    max: z.number().int().min(1).max(MAX_OPEN_FOLLOW_UPS).default(MAX_OPEN_FOLLOW_UPS),
    provider: AgentProviderSpec.nullable().optional(),
});
/** The value of a `followup:<question_key>` proposal from /v1/followups/propose. */
export const AgentFollowUpValue = z.object({
    reason: z.enum(FOLLOW_UP_REASONS),
    prompt: z.string().min(1).max(500),
    quote: z.string().max(500).nullable().default(null),
    related: z.string().max(200).nullable().default(null),
    controls: z.array(z.string().max(16)).min(1).max(5),
});
export const InterpretReplyRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    language: z.enum(['en', 'ms']).default('en'),
    question_key: z.string().min(1).max(64),
    question: z.string().min(1).max(500),
    options: z
        .array(z.strictObject({ value: z.string().max(64), label: z.string().max(200) }))
        .min(1)
        .max(50),
    current: z.string().max(64).nullable(),
    follow_up: z.string().min(1).max(500),
    reply: z.string().min(1).max(2000),
    provider: AgentProviderSpec.nullable().optional(),
});
//# sourceMappingURL=follow-ups.js.map